import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import type { Adjunto, FotoDeGaleria } from './api';

/**
 * piezas.tsx — las formas que Novedades repite.
 *
 * Nada de aquí sale del archivo salvo lo que la tarjeta de publicación necesita,
 * y la tarjeta sí sale: la monta también Comunidades a través del puente. Lo que
 * se comparte con el resto del portal vive en `ui/` y `components/`.
 */

/* ── Los tipos de publicación ──────────────────────────────────────────── */

/**
 * Icono, palabra y color de cada tipo.
 *
 * **Un solo token por tipo**, y ese es el cambio frente al sistema anterior. La
 * versión vanilla llevaba TRES valores por tipo —un hexadecimal para el filete,
 * un `rgba()` literal para el tinte y un token para la tinta— y de ahí salían
 * los 23 hexadecimales y los 17 `rgba()` escritos a mano de `page-feed.js`.
 * Ninguno de ellos se invertía en tema oscuro salvo por casualidad.
 *
 * Aquí el chip se pinta `text-<token>` sobre `bg-<token>/12`. Medido el
 * 2026-09-08 en los siete tipos y en los dos temas: entre 4,75 y 6,15.
 *
 * `galeria` no está: nadie escribe ese `post_type` —las galerías son siempre
 * `evento_galeria`, atadas a un evento— y su estilo no llegaba a usarse. El
 * valor sigue en el enum de la base como RESERVADO; quitarlo de ahí exigiría
 * migración.
 */
export const TIPOS: Record<string, { icono: string; etiqueta: string; color: string }> = {
  cumpleanos: { icono: 'bi-cake2-fill', etiqueta: 'Celebración', color: 'pink-ink' },
  evento: { icono: 'bi-calendar-event-fill', etiqueta: 'Evento', color: 'info-ink' },
  evento_galeria: { icono: 'bi-images', etiqueta: 'Galería', color: 'purple-ink' },
  encuesta: { icono: 'bi-clipboard2-check-fill', etiqueta: 'Encuesta', color: 'teal-ink' },
  logro: { icono: 'bi-trophy-fill', etiqueta: 'Logro', color: 'warn-ink' },
  bienestar: { icono: 'bi-heart-pulse-fill', etiqueta: 'Bienestar', color: 'ok-ink' },
  comunicado: { icono: 'bi-megaphone-fill', etiqueta: 'Comunicado', color: 'danger-ink' },
};

/** Las clases de cada tipo, escritas enteras para que Tailwind las vea. Una
 *  clase construida con plantilla (`text-${color}`) no aparece en el CSS
 *  generado: el compilador escanea texto, no evalúa expresiones. */
const CLASES_DE_TIPO: Record<string, string> = {
  'pink-ink': 'text-pink-ink bg-pink-ink/12',
  'info-ink': 'text-info-ink bg-info-ink/12',
  'purple-ink': 'text-purple-ink bg-purple-ink/12',
  'teal-ink': 'text-teal-ink bg-teal-ink/12',
  'warn-ink': 'text-warn-ink bg-warn-ink/12',
  'ok-ink': 'text-ok-ink bg-ok-ink/12',
  'danger-ink': 'text-danger-ink bg-danger-ink/12',
};

/** El chip de tipo de la cabecera. `general` no lleva ninguno: es el caso por
 *  defecto y etiquetarlo sería ruido en la mayoría de las tarjetas. */
export function ChipDeTipo({ tipo }: { tipo: string }) {
  const meta = TIPOS[tipo];
  if (!meta) return null;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-xs px-2 py-0.5',
        'text-1 font-bold uppercase tracking-wide',
        CLASES_DE_TIPO[meta.color],
      )}
    >
      <i className={cn('bi', meta.icono)} aria-hidden="true" />
      {meta.etiqueta}
    </span>
  );
}

/* ── Las reacciones ────────────────────────────────────────────────────── */

export const REACCIONES = [
  { clave: 'like', apagado: 'bi-hand-thumbs-up', activo: 'bi-hand-thumbs-up-fill', etiqueta: 'Me gusta' },
  { clave: 'heart', apagado: 'bi-heart', activo: 'bi-heart-fill', etiqueta: 'Encanta' },
  { clave: 'celebrate', apagado: 'bi-stars', activo: 'bi-stars', etiqueta: 'Celebrar' },
] as const;

/* ── Contenido enriquecido ─────────────────────────────────────────────── */

// `ContenidoRico` se mudó a `components/` el 2026-09-25, cuando Moderación
// empezó a necesitarlo para enseñar lo que alguien denunció. Se re-exporta desde
// aquí para no tocar los tres sitios de Novedades que ya lo importaban de este
// archivo.
export { ContenidoRico } from '@/components/ContenidoRico';

/* ── Editor enriquecido ────────────────────────────────────────────────── */

/** Los seis colores de la barra del compositor, tal cual los tenía el vanilla. */
const COLORES_DE_TEXTO: [string, string][] = [
  ['#0D2B4E', 'Navy'],
  ['#495057', 'Gris'],
  ['#C0392B', 'Rojo'],
  ['#1C7F46', 'Verde'],
  ['#2980B9', 'Azul'],
  ['#8E44AD', 'Morado'],
];

/**
 * El editor de texto con formato, con menciones.
 *
 * Sigue siendo un `contenteditable` con `document.execCommand`, como el vanilla:
 * `execCommand` está obsoleto pero es lo único que da negrita y color sin
 * arrastrar un editor entero al bundle, y el resultado pasa igualmente por
 * `Utils.sanitizeRich` antes de salir.
 *
 * **React no gobierna el contenido de este nodo** — un `contenteditable`
 * controlado se pelea con el cursor en cada pulsación. Lo que hace el
 * componente es leerlo cuando se lo piden (`onCambio`) y vaciarlo cuando el
 * padre sube la `versión`: así «publicar y limpiar» funciona sin que React
 * reescriba el DOM mientras alguien escribe.
 *
 * `mousedown` con `preventDefault` en cada botón de la barra: sin eso, pulsar
 * negrita quita el foco del editor y se pierde la selección justo antes de
 * aplicarle el formato.
 */
export function EditorRico({
  placeholder, onCambio, onFoco, version, etiqueta,
}: {
  placeholder: string;
  /** Recibe el texto plano en cada pulsación, para el contador y el aviso de
   *  lenguaje. El HTML se lee al enviar, con `leerEditor`. */
  onCambio: (texto: string) => void;
  /** Al enfocar. Lo usa el compositor para traer la lista de palabras vetadas
   *  justo cuando alguien va a escribir, y no a todo el que entra a leer. */
  onFoco?: () => void;
  /** Al cambiar, el editor se vacía. Es como el padre dice «ya se publicó». */
  version: number;
  etiqueta: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';
    // Idempotente por dentro: `Mentions` marca el elemento y no se re-engancha.
    Mentions.enable(el);
  }, [version]);

  const mandar = (cmd: string, valor?: string) => {
    ref.current?.focus();
    // styleWithCSS: que foreColor genere <span style="color"> y no <font>, que
    // el saneador tendría que convertir.
    try {
      document.execCommand('styleWithCSS', false, 'true');
    } catch {
      /* Firefox antiguo. El saneador convierte el <font> igualmente. */
    }
    document.execCommand(cmd, false, valor);
  };

  return (
    <div>
      <div
        className="mb-1.5 flex flex-wrap items-center gap-1 rounded-sm border border-line
                   bg-sunken px-1.5 py-1"
        role="toolbar"
        aria-label="Formato del texto"
      >
        {[
          { cmd: 'bold', icono: 'bi-type-bold', nombre: 'Negrita' },
          { cmd: 'italic', icono: 'bi-type-italic', nombre: 'Cursiva' },
          { cmd: 'underline', icono: 'bi-type-underline', nombre: 'Subrayado' },
        ].map((b) => (
          <Button
            key={b.cmd}
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={b.nombre}
            title={b.nombre}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => mandar(b.cmd)}
          >
            <i className={cn('bi', b.icono)} aria-hidden="true" />
          </Button>
        ))}

        <span className="mx-0.5 h-5 w-px bg-line" aria-hidden="true" />

        {COLORES_DE_TEXTO.map(([color, nombre]) => (
          <button
            key={color}
            type="button"
            // 24 px de caja con el punto de color dentro: en pantalla de dedo
            // sube a 44, que es el suelo del sistema.
            className="grid size-6 shrink-0 place-items-center rounded-xs
                       transition-colors hover:bg-line focus-visible:focus-anillo
                       pointer-coarse:size-11"
            aria-label={`Texto en ${nombre.toLowerCase()}`}
            title={nombre}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => mandar('foreColor', color)}
          >
            <span
              className="size-4 rounded-full border border-line"
              style={{ background: color }}
              aria-hidden="true"
            />
          </button>
        ))}

        <span className="mx-0.5 h-5 w-px bg-line" aria-hidden="true" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Quitar formato"
          title="Quitar formato"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => mandar('removeFormat')}
        >
          <i className="bi bi-eraser" aria-hidden="true" />
        </Button>
      </div>

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={etiqueta}
        data-marcador={placeholder}
        onInput={(e) => onCambio((e.currentTarget.innerText || '').trim())}
        onFocus={onFoco}
        className="editor-rico min-h-20 w-full rounded-sm border border-field-border
                   bg-surface px-3 py-2 text-3 leading-relaxed text-ink
                   focus-visible:focus-anillo"
      />
    </div>
  );
}

/**
 * Lee el editor: HTML saneado y texto. Se llama al enviar, no en cada tecla.
 *
 * Acepta el `contenteditable` o cualquier contenedor suyo, y busca dentro. Es a
 * propósito: el compositor tiene el `ref` en el envoltorio —dentro va también la
 * barra de formato— y leer el `innerHTML` de ese envoltorio se llevaría los
 * botones de negrita a la publicación.
 */
export function leerEditor(nodo: HTMLElement | null) {
  const el = nodo?.classList.contains('editor-rico')
    ? nodo
    : (nodo?.querySelector<HTMLElement>('.editor-rico') ?? null);
  if (!el) return { html: '', texto: '' };
  return {
    html: Utils.sanitizeRich(el.innerHTML).trim(),
    texto: (el.innerText || '').trim(),
  };
}

/* ── Adjuntos ──────────────────────────────────────────────────────────── */

/** El icono que le toca a un archivo por su tipo o su extensión. */
export function iconoDeArchivo(tipo?: string | null, nombre?: string | null) {
  const n = (nombre || '').toLowerCase();
  if ((tipo || '').startsWith('image/')) return 'bi-file-image';
  if (tipo === 'application/pdf' || n.endsWith('.pdf')) return 'bi-file-pdf';
  if (/\.(xlsx|xls|csv)$/.test(n)) return 'bi-file-spreadsheet';
  if (/\.docx?$/.test(n)) return 'bi-file-word';
  return 'bi-file-earmark';
}

/** El peso de un archivo, en la unidad que se lee de un vistazo. */
export function pesoLegible(bytes?: number | null) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Los archivos de una publicación: las imágenes como miniaturas, el resto como
 * filas con su peso.
 *
 * Las miniaturas abren el visor del portal (`Utils.openLightbox`), el mismo de
 * Eventos y de la galería, y no un modal propio: así la tira de miniaturas y las
 * flechas del teclado funcionan igual en las tres pantallas.
 */
export function Adjuntos({
  adjuntos, puedeBorrar, onBorrar,
}: {
  adjuntos: Adjunto[];
  puedeBorrar: boolean;
  onBorrar: (adjunto: Adjunto) => void;
}) {
  if (!adjuntos.length) return null;
  const imagenes = adjuntos.filter((a) => a.is_image);
  const documentos = adjuntos.filter((a) => !a.is_image);

  return (
    <div className="flex flex-col gap-2 px-4 pt-3">
      {imagenes.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {imagenes.map((a, i) => (
            <li key={a.id} className="relative">
              <button
                type="button"
                onClick={() => Utils.openLightbox(imagenes.map((x) => ({ url: x.url })), i)}
                className="block size-[90px] overflow-hidden rounded-sm border border-line
                           focus-visible:focus-anillo"
                aria-label={`Ver ${a.filename}`}
              >
                <img src={a.url} loading="lazy" alt="" className="size-full object-cover" />
              </button>
              {puedeBorrar && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  className="absolute right-0.5 top-0.5 size-6 rounded-full bg-surface/90"
                  aria-label={`Eliminar ${a.filename}`}
                  onClick={() => onBorrar(a)}
                >
                  <i className="bi bi-x-lg text-1" aria-hidden="true" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {documentos.map((a) => (
        <div
          key={a.id}
          className="flex items-center gap-3 rounded-sm border border-line bg-sunken px-3 py-2"
        >
          <i
            className={cn('bi shrink-0 text-6 text-brand-ink', iconoDeArchivo(a.content_type, a.filename))}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-3 font-semibold text-ink">{a.filename}</p>
            <p className="text-1 text-muted">{pesoLegible(a.size_bytes)}</p>
          </div>
          {/* Un enlace y no un botón: se puede abrir en pestaña nueva y copiar.
              El `download` deja que el navegador decida si lo enseña o lo baja. */}
          <a
            href={a.url}
            download={a.filename}
            target="_blank"
            rel="noopener"
            aria-label={`Descargar ${a.filename}`}
            title={`Descargar ${a.filename}`}
            className="grid size-9 shrink-0 place-items-center rounded-sm border-2 border-brand-ink
                       text-brand-ink transition-colors hover:bg-brand hover:text-on-brand
                       focus-visible:focus-anillo pointer-coarse:size-11"
          >
            <i className="bi bi-download" aria-hidden="true" />
          </a>
          {puedeBorrar && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={`Eliminar ${a.filename}`}
              onClick={() => onBorrar(a)}
            >
              <i className="bi bi-trash3" aria-hidden="true" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Galería de evento ─────────────────────────────────────────────────── */

/** Las fotos de una galería, hasta seis y un «+N» en la última. */
export function Galeria({ fotos }: { fotos: FotoDeGaleria[] }) {
  const visibles = fotos.slice(0, 6);
  const resto = fotos.length - visibles.length;
  return (
    <ul className="mt-3 grid grid-cols-3 gap-0.5">
      {visibles.map((g, i) => (
        <li key={i} className="relative aspect-square">
          <button
            type="button"
            onClick={() => Utils.openLightbox(fotos, i)}
            className="block size-full overflow-hidden focus-visible:focus-anillo"
            aria-label={`Ver foto ${i + 1} de ${fotos.length}`}
          >
            <img
              src={g.thumb_url || g.url}
              loading="lazy"
              alt=""
              className="size-full object-cover"
            />
            {i === visibles.length - 1 && resto > 0 && (
              <span
                className="absolute inset-0 grid place-items-center bg-brand/65 text-6
                           font-extrabold text-on-brand"
                aria-hidden="true"
              >
                +{resto}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
